import { ApiError } from './errors'

const KEY_BY_CODE: Record<string, string> = {
  VALIDATION_ERROR: 'errors.validation',
  UNAUTHORIZED: 'errors.unauthorized',
  FORBIDDEN: 'errors.forbidden',
  NOT_FOUND: 'errors.notFound',
  CONFLICT: 'errors.conflict',
  RATE_LIMITED: 'errors.rateLimited',
  INTERNAL_ERROR: 'errors.internal',
  PROVIDER_NOT_CONFIGURED: 'errors.providerNotConfigured',
  NETWORK_ERROR: 'errors.network',
  TIMEOUT: 'errors.timeout',
  OFFLINE: 'errors.offline',
  ABORTED: 'errors.unknown',
  UNKNOWN: 'errors.unknown',
}

const KEY_BY_STATUS: Record<number, string> = {
  400: 'errors.validation',
  401: 'errors.unauthorized',
  403: 'errors.forbidden',
  404: 'errors.notFound',
  409: 'errors.conflict',
  429: 'errors.rateLimited',
}

/**
 * Maps an unknown failure to an i18n key. Components call `t(key)`, so the
 * client never hard-codes user-facing copy and never leaks server internals.
 */
export function friendlyErrorKey(error: unknown): string {
  if (error instanceof ApiError) {
    return KEY_BY_CODE[error.code] ?? KEY_BY_STATUS[error.status] ?? 'errors.unknown'
  }

  if (error instanceof DOMException && error.name === 'AbortError') {
    return 'errors.unknown'
  }

  if (error instanceof TypeError) {
    return 'errors.network'
  }

  return 'errors.unknown'
}

/**
 * Backend-provided, already-localized error copy. Sources that render catalog
 * keys (`t(friendlyErrorKey(error))`) can fall back to this text so domain
 * validation details (activity fields, profile, waitlist rules) still reach
 * the user instead of the generic `errors.validation` fallback. It is only
 * populated when the response carried the server's own message, never by
 * client-side network/timeout bubbles.
 */
export function serverMessage(error: unknown): string | null {
  if (error instanceof ApiError) {
    const text = error.responseMessage?.trim() ?? ''
    return text !== '' ? text : null
  }
  return null
}

export function isUnauthorized(error: unknown): boolean {
  return error instanceof ApiError && error.isUnauthorized
}
